<script lang="ts">
	//Stores
	import { auth_locked_devices, pin_unlocked_devices } from 'src/stores';
	import { devices } from 'src/stores';
	import { os } from 'src/stores';
	//Services
	import { format } from 'svelte-i18n';
	import { zwaveMultilevelSwitchDirection } from '../../../services/xz_http_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Helpers
	import {
		get_z_wave_channel_structure_from_category,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Design system components
	import { ControlsContainer, DeviceButtonV2, Text, toast } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';

	export let device, status;

	let isLoadingOpen = false;
	let isLoadingClose = false;
	let direction: 'opening' | 'closing' | 'stopped' | undefined;
	let pinLocked;
	let showModal = false;
	let callbackValue;

	$: hasRights = hasControlRights(device);
	$: component = get_z_wave_channel_structure_from_category(device);
	$: coverStatus = status?.[component];
	$: coverState = coverStatus?.state;
	$: btn1 = coverState === 'opening' || direction === 'opening';
	$: btn2 = coverState === 'closing' || direction === 'closing';
	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: if (coverState === 'stopped' || coverState === 'open' || coverState === 'closed') {
		direction = undefined;
	}

	const handleDirectionRequest = async (dir: 0 | 1) => {
		const isAlreadyMovingSameWay =
			(dir === 1 && (coverState === 'opening' || direction === 'opening')) ||
			(dir === 0 && (coverState === 'closing' || direction === 'closing'));

		const nextDirection = isAlreadyMovingSameWay ? 'stop' : dir === 1 ? 'open' : 'close';
		direction =
			nextDirection === 'open' ? 'opening' : nextDirection === 'close' ? 'closing' : 'stopped';

		if (nextDirection === 'open') {
			isLoadingOpen = true;
		} else if (nextDirection === 'close') {
			isLoadingClose = true;
		}

		const res = await zwaveMultilevelSwitchDirection(device, nextDirection);

		if (!res.isok && $auth_locked_devices.indexOf(real_id(device.id)) === -1) {
			direction = undefined;
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		setTimeout(() => {
			isLoadingOpen = false;
			isLoadingClose = false;
		}, 100);
	};

	const handleRequest = async (dir: 0 | 1) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			handleDirectionRequest(dir);
		} else {
			callbackValue = dir;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;
	};
</script>

<ControlsContainer color="color-0">
	<svelte:fragment slot="first">
		<DeviceButtonV2
			id="devicebutton-ltmr2gt"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			isActive={btn1}
			aria-pressed={btn1}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			size="l"
			isLoading={isLoadingOpen}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
			disabled={!hasRights}
		/>
		<Text styleType="controls-large" class="mt-s-plus text-bold">
			{$format('text_device_control_direction_open', {
				default: 'Open',
			})}
		</Text>
	</svelte:fragment>
	<svelte:fragment slot="second">
		<DeviceButtonV2
			id="devicebutton-2fqnj7o"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			isActive={btn2}
			aria-pressed={btn2}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			size="l"
			isLoading={isLoadingClose}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
			disabled={!hasRights}
		/>
		<Text styleType="controls-large" class="mt-s-plus text-bold">
			{$format('text_device_control_direction_close', {
				default: 'Close',
			})}
		</Text>
	</svelte:fragment>
</ControlsContainer>

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
